import { consoleRoutes, defaultWelcome } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Skeleton, Textarea, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { WidgetOut } from "../../deploy-knowledge/shared/types";
import { usePublish, useSaveDraft, useWidget } from "../../deploy-knowledge/widget-studio/use-widget-studio";
import { storedLeadFields, toDraftIn, toWidgetForm, type WidgetForm } from "../../deploy-knowledge/widget-studio/widget-model";
import type { AgentConfig } from "../shared/types";
import { SettingsCard } from "./settings-ui";

const K = "build.agentSettings.greeting";
const MAX_CHARS = 500;

/** Everything in the widget config except the welcome message (what "other pending changes" means). */
function withoutWelcome(form: WidgetForm): WidgetForm {
  return { ...form, content: { ...form.content, welcome_message: "" } };
}

/** The draft differs from what visitors get in more than the welcome message. */
export function hasOtherPendingChanges(widget: WidgetOut): boolean {
  if (!widget.published) return false;
  const draft = JSON.stringify(withoutWelcome(toWidgetForm(widget.draft)));
  const live = JSON.stringify(withoutWelcome(toWidgetForm(widget.published.config)));
  return draft !== live;
}

function GreetingEditor({ agentId, config, widget, canEdit }: { agentId: string; config: AgentConfig; widget: WidgetOut; canEdit: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const message = useErrorMessage();
  const save = useSaveDraft(agentId);
  const publish = usePublish(agentId);
  const saved = toWidgetForm(widget.draft).content.welcome_message;
  const [value, setValue] = useState(saved);
  const preview = defaultWelcome({ assistantName: config.name, orgName: summary.name, languages: config.languages });
  const busy = save.isPending || publish.isPending;
  const dirty = value.trim() !== saved;

  const onSave = async () => {
    const form = toWidgetForm(widget.draft);
    const goLive = widget.published !== null && !hasOtherPendingChanges(widget);
    try {
      const next = { ...form, content: { ...form.content, welcome_message: value.trim() } };
      await save.mutateAsync(toDraftIn(next, storedLeadFields(widget.draft)));
      if (goLive) await publish.mutateAsync();
      toast.success(t(widget.published !== null && !goLive ? `${K}.savedDraft` : `${K}.saved`));
    } catch (error) {
      toast.error(message(error));
    }
  };

  return (
    <>
      <Field label={t(`${K}.label`)} hint={value.trim() ? undefined : t(`${K}.defaultNote`, { preview })}>
        <Textarea rows={3} maxLength={MAX_CHARS} disabled={!canEdit || busy} value={value} placeholder={preview} onChange={(event) => { setValue(event.target.value); }} />
      </Field>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-fg-subtle">
          {t(`${K}.startersNote`)}{" "}
          <AppLink href={consoleRoutes.widgetStudio(summary.slug, agentId, "content")} className="font-semibold text-primary-ink hover:underline">
            {t(`${K}.studio`)}
          </AppLink>
        </p>
        {canEdit ? (
          <Button type="button" size="sm" loading={busy} disabled={!dirty || busy} onClick={() => void onSave()}>
            {t(`${K}.save`)}
          </Button>
        ) : null}
      </div>
    </>
  );
}

/** Welcome message: the assistant's first bubble on every channel (stored with the widget content). */
export function GreetingCard({ config, disabled }: { config: AgentConfig; disabled: boolean }) {
  const { t } = useTranslation();
  const permissions = usePermissions(config.id);
  const widget = useWidget(config.id);
  const canEdit = !disabled && !permissions.readOnly && permissions.can("widget.manage", { agentId: config.id });
  return (
    <SettingsCard title={t(`${K}.title`)} hint={t(`${K}.hint`)}>
      {widget.isPending ? <Skeleton className="h-24" /> : null}
      {widget.isError ? <p className="text-sm text-fg-muted">{t(`${K}.unavailable`)}</p> : null}
      {widget.data ? <GreetingEditor key={JSON.stringify(widget.data.draft.content)} agentId={config.id} config={config} widget={widget.data} canEdit={canEdit} /> : null}
    </SettingsCard>
  );
}
